/**
 * « Mes élèves » — le tableau de consultation, en DEUXIÈME rang du rail.
 *
 * Ce n'est plus l'écran d'entrée (§10.1) : il sert la consultation et la
 * recherche, pas la décision du matin. La maquette v5 ne le dessine pas — elle
 * s'arrête à « Ouvrir la liste » — donc il est construit ici d'après les trois
 * corrections écrites en §10.3, sur les données réelles de
 * `GET /v1/tuteur/eleves` :
 *
 *  1. **Les sparklines de progression sont remplacées par les trois paniers
 *     DÉNOMBRÉS** (`PaniersSavoirsTuteurDto` : acquis / en cours / à revoir).
 *     Une sparkline sans échelle ni légende n'est pas de l'information, et un
 *     pourcentage de maîtrise n'existe pas au contrat.
 *  2. **La dose ne porte aucun objectif chiffré** — `DoseEleveResponse` ne
 *     connaît aucune durée cible. On montre les jours actifs sur sept jours et
 *     la dernière séance, rien de plus.
 *  3. **Le tri affiche son critère.** Le tri par défaut est celui qui sert la
 *     consultation — les élèves sans séance récente d'abord — et la phrase qui
 *     le dit est sous le tableau, pas dans une infobulle.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { nommerJoursDePratique, phraseSavoirsObserves, type EleveDuTuteurDto } from '@silex/shared';

import { api, ApiError } from '../api/client';
import { Chargement, ErreurRessource } from '../components/EtatRessource';
import { EmptyState } from '../components/EmptyState';
import { matiereLabel } from '../lib/suivi-format';
import {
  formatDate,
  formatJoursSansSeance,
  initiale,
  libelleNiveauScolaire,
  pluriel,
} from '../lib/tuteur-format';
import { manuelVivantActif } from '../lib/manuel-vivant';
import { useTuteur } from '../tuteur/TuteurContext';

import { ElevesParUrgence } from './mes-eleves/ElevesParUrgence';

type Tri = 'attention' | 'prenom';

const CRITERES_TRI: Readonly<Record<Tri, string>> = {
  attention:
    "Tri par défaut : les élèves qui ont une évaluation en attente d'abord, puis ceux dont la dernière séance est la plus ancienne. Un élève jamais actif est traité comme le plus ancien.",
  prenom: 'Tri alphabétique par prénom. Aucun classement, aucun score.',
};

function rangAttention(e: EleveDuTuteurDto): number {
  // Jamais de séance = le plus ancien possible ; sinon, jours écoulés.
  return e.joursSansSeance === null ? Number.MAX_SAFE_INTEGER : e.joursSansSeance;
}

function trier(eleves: readonly EleveDuTuteurDto[], tri: Tri): EleveDuTuteurDto[] {
  const copie = [...eleves];
  if (tri === 'prenom') return copie.sort((a, b) => a.prenom.localeCompare(b.prenom, 'fr'));
  return copie.sort((a, b) => {
    if (a.nbEvaluationsAValider !== b.nbEvaluationsAValider) {
      return b.nbEvaluationsAValider - a.nbEvaluationsAValider;
    }
    return rangAttention(b) - rangAttention(a);
  });
}

/**
 * AJOUTER UN ÉLÈVE PAR SON CODE DE SUIVI.
 *
 * Le fondateur, 14 septembre 2026 : « ajouter un élève à son suivi en utilisant
 * un code de suivi style NIP offert par l'élève (qu'il a copié depuis son
 * app) ». C'est la SEULE porte d'entrée d'un élève dans le périmètre d'un
 * professeur : créer un élève depuis la console a été écarté, parce qu'il aurait
 * fallu rendre `Eleve.parentId` nullable — c'est-à-dire toucher au locataire
 * dont dépend toute l'isolation des données.
 *
 * La saisie part telle qu'elle est recopiée : tirets, espaces et casse sont
 * normalisés côté serveur. Refuser une saisie juste serait la pire façon
 * d'échouer.
 */
function RattacherParCode({
  onRattache,
  dansUnRabat = false,
}: {
  onRattache: () => void;
  /**
   * Revue UX `UX-7-33` — dans la liste par « pourquoi maintenant », le
   * formulaire vit DANS le rabat « Ajouter un élève » : ni seconde carte, ni
   * titre répété.
   */
  dansUnRabat?: boolean;
}) {
  const [code, setCode] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErreur(null);
    setSucces(null);
    setEnvoi(true);
    try {
      const r = await api.rattacherParCodeSuivi(code);
      setSucces(
        r.deja
          ? `${r.prenom} faisait déjà partie de votre suivi.`
          : `${r.prenom} rejoint votre suivi.`,
      );
      setCode('');
      onRattache();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Erreur inattendue');
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <form className={dansUnRabat ? undefined : 'card'} onSubmit={onSubmit} noValidate>
      {dansUnRabat ? null : <h3>Ajouter un élève</h3>}
      <p className="card-sub">
        L’élève affiche son code de suivi dans son application et vous le donne. Il vaut
        vingt-quatre heures et ne sert qu’une fois.
      </p>

      {erreur ? (
        <p className="callout callout--rare" role="alert">
          {erreur}
        </p>
      ) : null}
      {succes ? (
        <p className="callout callout--ok" role="status">
          {succes}
        </p>
      ) : null}

      <div className="filters">
        <label className="field field--inline">
          <span>Code de suivi</span>
          <input
            className="control"
            value={code}
            placeholder="ABCD-2345"
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setCode(e.target.value)}
          />
        </label>
        <button type="submit" className="btn btn--primary" disabled={envoi || code.trim() === ''}>
          {envoi ? 'Rattachement…' : 'Ajouter à mon suivi'}
        </button>
      </div>
    </form>
  );
}

/**
 * Chantier « manuel vivant », Φ-MV-5 (MV-TUT-01) — DEUX LISTES, UNE ADRESSE.
 *
 * Capacité allumée : la liste par « pourquoi maintenant », dont l'ordre et la
 * raison de chaque ligne viennent du serveur (`pages/mes-eleves/`). Capacité
 * éteinte (build ou compte) : le tableau de consultation d'avant, À
 * L'IDENTIQUE — il n'appelle aucune route du chantier
 * (`mes-eleves/mes-eleves.test.tsx` le tient).
 */
export function MesElevesPage() {
  const { rechargerEleves } = useTuteur();
  if (!manuelVivantActif()) return <ListeDeConsultation />;
  return (
    <ElevesParUrgence
      ajout={(surRattache) => (
        <RattacherParCode
          dansUnRabat
          onRattache={() => {
            rechargerEleves();
            surRattache();
          }}
        />
      )}
    />
  );
}

function ListeDeConsultation() {
  const { eleves, rechargerEleves } = useTuteur();
  const [params, setParams] = useSearchParams();
  const [tri, setTri] = useState<Tri>('attention');
  const q = params.get('q') ?? '';

  const lignes = useMemo(() => {
    if (eleves.status !== 'ready') return [];
    const filtre = q.trim().toLocaleLowerCase('fr');
    const retenus = filtre
      ? eleves.data.eleves.filter((e) => e.prenom.toLocaleLowerCase('fr').includes(filtre))
      : eleves.data.eleves;
    return trier(retenus, tri);
  }, [eleves, q, tri]);

  return (
    <div className="content__inner">
      <div className="page-head">
        <div>
          <h1>Mes élèves</h1>
          <p className="page-sub">
            La consultation et la recherche. Les décisions du jour se prennent dans{' '}
            <Link to="/ma-journee">Ma journée</Link> — un tableau présente, il ne priorise pas.
          </p>
        </div>
      </div>

      <RattacherParCode onRattache={rechargerEleves} />

      {eleves.status === 'loading' ? <Chargement quoi="votre périmètre" /> : null}
      {eleves.status === 'error' ? <ErreurRessource message={eleves.message} /> : null}

      {eleves.status === 'ready' ? (
        <>
          <div className="filters">
            <label className="field field--inline">
              <span className="lbl sr-only">Rechercher un élève</span>
              <input
                className="control"
                placeholder="Rechercher un élève…"
                value={q}
                onChange={(e) => {
                  const valeur = e.target.value;
                  setParams(valeur ? { q: valeur } : {}, { replace: true });
                }}
              />
            </label>
            <div className="segmented" role="group" aria-label="Tri">
              <button
                type="button"
                className={tri === 'attention' ? 'segmented__btn is-active' : 'segmented__btn'}
                onClick={() => setTri('attention')}
              >
                Attention
              </button>
              <button
                type="button"
                className={tri === 'prenom' ? 'segmented__btn is-active' : 'segmented__btn'}
                onClick={() => setTri('prenom')}
              >
                Prénom
              </button>
            </div>
          </div>

          {eleves.data.nbEleves === 0 ? (
            /* LOT 3.4 — cet état vide renvoyait à « un administrateur de
               l'organisation » et citait une route d'API à un tuteur, qui n'a
               ni l'écran ni le droit de l'appeler. Il bouclait un cul-de-sac :
               l'administrateur en question n'avait pas l'outil non plus
               (CON-ADM-CRE-02). L'écran existe désormais — Administration →
               Affectations — et la phrase peut enfin dire à qui s'adresser
               sans citer un verbe HTTP. */
            <EmptyState title="Aucun élève ne vous est affecté">
              C'est un administrateur qui rattache un élève à votre compte, depuis Administration →
              Affectations. Tant qu'aucune affectation active n'existe, cette console est vide — ce
              n'est pas une panne, et il n'y a rien à réessayer.
            </EmptyState>
          ) : null}

          {eleves.data.nbEleves > 0 && lignes.length === 0 ? (
            <EmptyState title="Aucun élève ne correspond à cette recherche">
              {pluriel(eleves.data.nbEleves, 'élève vous est affecté', 'élèves vous sont affectés')}
              . Effacez la recherche pour les revoir tous.
            </EmptyState>
          ) : null}

          {lignes.length > 0 ? (
            <>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th scope="col">Élève</th>
                      <th scope="col">Matières</th>
                      <th scope="col">Dernière séance</th>
                      <th scope="col">Sept derniers jours</th>
                      <th scope="col">Savoirs observés</th>
                      <th scope="col">À valider</th>
                      <th scope="col">
                        <span className="sr-only">Ouvrir la fiche</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {lignes.map((eleve) => (
                      <LigneEleve key={eleve.eleveId} eleve={eleve} />
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="note-line">{CRITERES_TRI[tri]}</p>
              <p className="note-line">
                Trois paniers dénombrés. Jamais un pourcentage, jamais une moyenne, jamais un niveau
                agrégé.
              </p>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function LigneEleve({ eleve }: { eleve: EleveDuTuteurDto }) {
  const p = eleve.paniers;
  return (
    <tr>
      <th scope="row">
        <span className="cell-eleve">
          <span className="avatar-sq avatar-sq--sm" aria-hidden="true">
            {initiale(eleve.prenom)}
          </span>
          <span>
            <Link to={`/eleves/${eleve.eleveId}`}>{eleve.prenom}</Link>
            <span className="cell-sub">{libelleNiveauScolaire(eleve.niveauScolaire)}</span>
          </span>
        </span>
      </th>
      <td>
        {eleve.matieresActives.length > 0
          ? eleve.matieresActives.map(matiereLabel).join(', ')
          : 'Aucune matière active'}
      </td>
      <td>
        {eleve.derniereSeance ? formatDate(eleve.derniereSeance) : 'Jamais'}
        <span className="cell-sub">{formatJoursSansSeance(eleve.joursSansSeance)}</span>
      </td>
      <td>
        <span className="data">{eleve.nbJoursActifs7}</span>
        {/* LOT 1.3 — « jours avec au moins une séance » nommait un objet
            (`SessionTravail`) que ce nombre ne compte pas : il compte des
            JOURS, toutes sources d'activité confondues. Le nom du fait est le
            même sur les quatre surfaces (`lib/jours-de-pratique.ts`). */}
        <span className="cell-sub">{nommerJoursDePratique(eleve.nbJoursActifs7)} sur sept</span>
      </td>
      <td>
        <span className="paniers">
          <span className="badge badge--acquis">{p.acquis} acquis</span>
          <span className="badge badge--en-cours">{p.enCours} en cours</span>
          <span className="badge badge--a-revoir">{p.aRevoir} à revoir</span>
        </span>
        {/* LOT 1.4 (BD-04) — la somme des trois paniers compte des MESURES :
            un savoir visuel se mesure sur deux choses et donne deux lignes.
            Elle était rendue sous le nom « savoirs observés », et les paniers
            dépliés montraient alors le même savoir deux fois. Deux chiffres,
            deux noms — jamais l'un divisé par l'autre. */}
        {/* LOT 1.6 — la phrase vit dans `@silex/shared/editorial`, pas ici. */}
        <span className="cell-sub">{phraseSavoirsObserves(p.nbSavoirsObserves, p.nbMesures)}</span>
      </td>
      <td>
        {eleve.nbEvaluationsAValider > 0 ? (
          <Link to="/validation" className="badge badge--new">
            {eleve.nbEvaluationsAValider}
          </Link>
        ) : (
          <span className="cell-sub">—</span>
        )}
      </td>
      <td>
        <Link className="btn btn--ghost btn--sm" to={`/eleves/${eleve.eleveId}`}>
          Ouvrir sa fiche
        </Link>
      </td>
    </tr>
  );
}
